<template>
  <svg
    v-if="custom"
    class="max-icon"
    :data-size="size"
    :data-style="custom.style ?? 'stroke'"
    :viewBox="custom.viewBox"
    :fill="custom.style === 'fill' ? 'currentColor' : 'none'"
    :stroke="custom.style === 'fill' ? 'none' : 'currentColor'"
    :stroke-width="custom.strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    :role="label ? 'img' : undefined"
    :aria-label="label || undefined"
    :aria-hidden="label ? undefined : 'true'"
  ><path :d="custom.path" /></svg>
  <component :is="hostIcon" v-else-if="hostIcon" :name="name" :label="label" class="max-icon" :data-size="size" />
  <svg
    v-else-if="path"
    class="max-icon"
    :data-size="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    :role="label ? 'img' : undefined"
    :aria-label="label || undefined"
    :aria-hidden="label ? undefined : 'true'"
  ><path :d="path" /></svg>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ICON_PATHS } from '../core/icons.ts'
import { CUSTOM_ICON_RE, ICON_PATH_RE, ICON_VIEWBOX_RE, MAX_ICON_PATH } from '../core/interface.ts'
import { useMaxContext, useMaxHostControl } from '../runtime/context.ts'

const props = defineProps<{ name: string, label?: string, size?: string }>()
const ctx = useMaxContext()
// A host may draw built-in icons with its own icon font by registering an 'icon' control
const hostIcon = useMaxHostControl(() => 'icon')
const path = computed(() => (ICON_PATHS as Record<string, string>)[props.name] ?? null)

/** The tool's own icon: path data only, re-checked here so nothing but a path is ever drawn */
const custom = computed(() => {
  const def = CUSTOM_ICON_RE.test(props.name) ? ctx.icons?.[props.name] : undefined
  if (!def || typeof def.path !== 'string' || def.path.length > MAX_ICON_PATH || !ICON_PATH_RE.test(def.path)) return null
  const viewBox = typeof def.viewBox === 'string' && ICON_VIEWBOX_RE.test(def.viewBox) ? def.viewBox : '0 0 24 24'
  const width = Number(viewBox.trim().split(/[\s,]+/)[2]) || 24
  // Outlines keep the built-in icons' 2/24 weight whatever box the path is drawn in
  return { path: def.path, viewBox, style: def.style === 'fill' ? 'fill' : 'stroke', strokeWidth: String(Math.round(width / 12 * 100) / 100) }
})
</script>
